/**
 * Maschere e validazioni dei campi elementari in formato italiano.
 *
 * **Perimetro rigorosamente limitato all'ingresso.** CONVENTIONS §2 stabilisce
 * che `api/app/core/formats.py` e' l'unico punto autorizzato a trasformare
 * numeri e date in stringhe destinate all'esterno: nei corpi JSON dell'API date
 * e importi viaggiano **gia' formattati**. Il back-office quindi non converte
 * mai in uscita — le stringhe che riceve le mostra e basta.
 *
 * Questo modulo serve al percorso opposto: aiutare l'operatore a **digitare** un
 * valore gia' nella forma che l'API si aspetta (`gg/mm/yyyy`, `1.456.456,58`,
 * `75,33`), e rifiutare in pagina quello che il server rifiuterebbe comunque.
 * Il valore inviato e' la stringa cosi' com'e' stata digitata e normalizzata:
 * non passa mai da `Number`, quindi non c'e' arrotondamento binario possibile.
 *
 * Riferimento della tabella dei formati: delibera ANAC 495/2024, «Formato dei
 * campi elementari».
 */

/** Esito di una validazione di campo: messaggio in italiano o `null` se valido. */
export type EsitoValidazione = string | null;

/** Rimuove tutto tranne le cifre. */
function soleCifre(valore: string): string {
  return valore.replace(/\D+/g, "");
}

// --------------------------------------------------------------------------- //
// Data — gg/mm/yyyy
// --------------------------------------------------------------------------- //

/**
 * Maschera progressiva per una data: inserisce le barre mentre si digita.
 *
 * `3` → `3`, `30` → `30/`, `3004` → `30/04/`, `30042026` → `30/04/2026`.
 * Le barre digitate a mano vengono assorbite, cosi' incollare `30/04/2026`
 * funziona quanto digitare `30042026`.
 */
export function mascheraData(grezzo: string): string {
  const cifre = soleCifre(grezzo).slice(0, 8);
  if (cifre.length <= 2) return cifre.length === 2 ? `${cifre}/` : cifre;
  if (cifre.length <= 4) {
    const mese = cifre.slice(2);
    return mese.length === 2 ? `${cifre.slice(0, 2)}/${mese}/` : `${cifre.slice(0, 2)}/${mese}`;
  }
  return `${cifre.slice(0, 2)}/${cifre.slice(2, 4)}/${cifre.slice(4)}`;
}

/** Converte `gg/mm/yyyy` in `Date`, verificando che il giorno esista davvero. */
export function dataDaTesto(valore: string): Date | null {
  const corrispondenza = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(valore.trim());
  if (!corrispondenza) return null;
  const giorno = Number(corrispondenza[1]);
  const mese = Number(corrispondenza[2]);
  const anno = Number(corrispondenza[3]);
  const data = new Date(anno, mese - 1, giorno);
  // Scarta il 31/02: `Date` lo trasformerebbe silenziosamente in marzo.
  if (data.getDate() !== giorno || data.getMonth() !== mese - 1 || data.getFullYear() !== anno) {
    return null;
  }
  return data;
}

/** Rende una `Date` come `gg/mm/yyyy`. Uso interno alla UI (default, confronti). */
export function testoDaData(data: Date): string {
  const giorno = String(data.getDate()).padStart(2, "0");
  const mese = String(data.getMonth() + 1).padStart(2, "0");
  return `${giorno}/${mese}/${data.getFullYear()}`;
}

/** Data odierna come `gg/mm/yyyy`. */
export function oggi(): string {
  return testoDaData(new Date());
}

/** Valida una data `gg/mm/yyyy`. */
export function validaData(valore: string): EsitoValidazione {
  if (!valore.trim()) return null;
  return dataDaTesto(valore) === null
    ? "Data non valida: usare il formato gg/mm/aaaa, per esempio 30/04/2026."
    : null;
}

/**
 * Giorni che separano `valore` da oggi (negativi se la data e' passata).
 * Accetta sia `gg/mm/yyyy` sia `gg/mm/yyyy hh:mm`.
 */
export function giorniDaOggi(valore: string): number | null {
  const data = dataDaTesto(valore.trim().slice(0, 10));
  if (!data) return null;
  const inizioOggi = new Date();
  inizioOggi.setHours(0, 0, 0, 0);
  return Math.round((data.getTime() - inizioOggi.getTime()) / 86_400_000);
}

// --------------------------------------------------------------------------- //
// Ora e data-ora
// --------------------------------------------------------------------------- //

/** Maschera per `hh:mm`. */
export function mascheraOra(grezzo: string): string {
  const cifre = soleCifre(grezzo).slice(0, 4);
  if (cifre.length <= 2) return cifre.length === 2 ? `${cifre}:` : cifre;
  return `${cifre.slice(0, 2)}:${cifre.slice(2)}`;
}

/** Valida un orario `hh:mm`. */
export function validaOra(valore: string): EsitoValidazione {
  if (!valore.trim()) return null;
  const corrispondenza = /^(\d{2}):(\d{2})$/.exec(valore.trim());
  if (!corrispondenza) return "Orario non valido: usare il formato hh:mm, per esempio 09:30.";
  const ore = Number(corrispondenza[1]);
  const minuti = Number(corrispondenza[2]);
  return ore > 23 || minuti > 59 ? "Orario inesistente." : null;
}

/** Maschera per `gg/mm/yyyy hh:mm`. */
export function mascheraDataOra(grezzo: string): string {
  const cifre = soleCifre(grezzo).slice(0, 12);
  const parteData = mascheraData(cifre.slice(0, 8));
  if (cifre.length <= 8) return parteData;
  return `${parteData} ${mascheraOra(cifre.slice(8))}`;
}

/** Valida `gg/mm/yyyy hh:mm`. */
export function validaDataOra(valore: string): EsitoValidazione {
  if (!valore.trim()) return null;
  const parti = valore.trim().split(/\s+/);
  if (parti.length !== 2) {
    return "Data e ora non valide: usare il formato gg/mm/aaaa hh:mm, per esempio 10/05/2026 11:00.";
  }
  return validaData(parti[0]) ?? validaOra(parti[1]);
}

// --------------------------------------------------------------------------- //
// Importi — n.nnn,dd
// --------------------------------------------------------------------------- //

/** Applica i punti delle migliaia a una stringa di sole cifre. */
function raggruppaMigliaia(cifre: string): string {
  return cifre.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

/**
 * Riconosce un importo scritto alla maniera anglosassone: virgola per le
 * migliaia, punto per i decimali (`1,456,456.58`).
 *
 * Serve a **non convertirlo**. Passandolo alla maschera, i punti verrebbero
 * scartati come separatori di migliaia e l'ultima virgola letta come decimale:
 * `1,456,456.58` diventerebbe `1.456,45`, che e' un importo perfettamente
 * valido, tre ordini di grandezza sotto quello vero. Su un dato contabile
 * destinato alla pubblicazione, un errore plausibile e' peggio di un errore
 * evidente: qui si preferisce fermarsi e far correggere all'operatore.
 *
 * Due sole forme sono inequivocabili e vengono riconosciute:
 *
 * * gruppi di migliaia separati da virgola **e** decimali dopo un punto
 *   (`1,456.58`, `1,456,456.58`);
 * * almeno due gruppi di migliaia separati da virgola, senza decimali
 *   (`1,456,456`).
 *
 * Un singolo `1,456` resta fuori: in italiano e' semplicemente un importo con
 * tre decimali, che la validazione respinge gia' per conto proprio.
 */
export function sembraImportoAnglosassone(grezzo: string): boolean {
  const testo = grezzo.replace(/[\s €]/g, "");
  if (!testo) return false;
  return (
    /^-?\d{1,3}(,\d{3})+\.\d{1,2}$/.test(testo) || /^-?\d{1,3}(,\d{3}){2,}$/.test(testo)
  );
}

/**
 * Maschera per un importo mentre si digita.
 *
 * Raggruppa le migliaia con il punto, tiene la virgola come separatore decimale
 * e tronca a due cifre. `1456456,58` diventa `1.456.456,58`; `1456456,5` resta
 * `1.456.456,5` e lo completa a `,50` {@link normalizzaImporto} quando il campo
 * perde il fuoco.
 *
 * **Il punto e' sempre un separatore di migliaia e viene scartato**, mai letto
 * come decimale. Non e' una semplificazione: e' la maschera stessa a inserire i
 * punti, quindi il campo si trova a rileggere di continuo il proprio output.
 * Se interpretasse il punto come decimale, `1.456` — cioe' millequattrocento -
 * cinquantasei appena digitato — tornerebbe indietro come `1,45`.
 *
 * Un valore incollato nel formato anglosassone viene invece lasciato **intatto**
 * — vedi {@link sembraImportoAnglosassone} — cosi' che `validaImporto` lo possa
 * respingere dicendo esattamente che cosa non va. Se compaiono piu' virgole in
 * un valore non riconoscibile come anglosassone vale come decimale l'ultima.
 */
export function mascheraImporto(grezzo: string): string {
  if (sembraImportoAnglosassone(grezzo)) return grezzo;
  const segno = grezzo.trimStart().startsWith("-") ? "-" : "";
  const parti = grezzo.replace(/[^\d,]/g, "").split(",");
  const decimale = parti.length > 1 ? (parti.pop() ?? "") : null;
  const intera = raggruppaMigliaia(parti.join("").replace(/^0+(?=\d)/, ""));
  if (decimale === null) return `${segno}${intera}`;
  return `${segno}${intera || "0"},${decimale.slice(0, 2)}`;
}

/**
 * Porta un importo alla forma canonica `n.nnn,dd` con due decimali.
 * Va chiamata quando il campo perde il fuoco, non a ogni tasto.
 *
 * Un valore anglosassone resta com'e': normalizzarlo significherebbe
 * convertirlo, cioe' proprio quello che si vuole evitare.
 */
export function normalizzaImporto(valore: string): string {
  if (sembraImportoAnglosassone(valore)) return valore;
  const mascherato = mascheraImporto(valore);
  if (!mascherato || mascherato === "-") return "";
  const [intera, decimale = ""] = mascherato.split(",");
  return `${intera || "0"},${decimale.padEnd(2, "0")}`;
}

/** Valida un importo in formato italiano. */
export function validaImporto(valore: string): EsitoValidazione {
  const testo = valore.trim();
  if (!testo) return null;
  if (sembraImportoAnglosassone(testo)) {
    return "Sembra un importo in formato anglosassone: qui le migliaia si separano con il punto e i decimali con la virgola (1.456.456,58). Lo riscriva, per non pubblicare una cifra sbagliata.";
  }
  return /^-?\d{1,3}(\.\d{3})*(,\d{1,2})?$/.test(testo) || /^-?\d+(,\d{1,2})?$/.test(testo)
    ? null
    : "Importo non valido: separatore delle migliaia il punto, decimale la virgola (1.456.456,58).";
}

// --------------------------------------------------------------------------- //
// Percentuali — nn,dd (senza simbolo)
// --------------------------------------------------------------------------- //

/**
 * Maschera per una percentuale: cifre e virgola, due decimali, niente simbolo.
 *
 * A differenza degli importi, qui **il punto viene convertito in virgola**: una
 * percentuale non supera 100 e non ha quindi separatori di migliaia, per cui un
 * punto digitato non puo' che essere il decimale. `75.33` diventa `75,33`.
 */
export function mascheraPercentuale(grezzo: string): string {
  const parti = grezzo.replace(/\./g, ",").replace(/[^\d,]/g, "").split(",");
  const decimale = parti.length > 1 ? (parti.pop() ?? "") : null;
  const intera = parti.join("").slice(0, 3);
  if (decimale === null) return intera;
  return `${intera || "0"},${decimale.slice(0, 2)}`;
}

/** Valida una percentuale `nn,dd`, ammettendo l'intervallo 0–100. */
export function validaPercentuale(valore: string): EsitoValidazione {
  const testo = valore.trim();
  if (!testo) return null;
  if (!/^\d{1,3}(,\d{1,2})?$/.test(testo)) {
    return "Percentuale non valida: usare la virgola come separatore decimale, senza simbolo (75,33).";
  }
  const intera = Number(testo.split(",")[0]);
  return intera > 100 ? "La percentuale non puo' superare 100." : null;
}

// --------------------------------------------------------------------------- //
// Anno, trimestre, semestre
// --------------------------------------------------------------------------- //

/** Maschera per un anno `nnnn`. */
export function mascheraAnno(grezzo: string): string {
  return soleCifre(grezzo).slice(0, 4);
}

/** Valida un anno di riferimento in un intervallo plausibile. */
export function validaAnno(valore: string): EsitoValidazione {
  const testo = valore.trim();
  if (!testo) return null;
  if (!/^\d{4}$/.test(testo)) return "Anno non valido: quattro cifre, per esempio 2026.";
  const anno = Number(testo);
  const massimo = new Date().getFullYear() + 1;
  return anno < 2000 || anno > massimo
    ? `Anno fuori intervallo: ammessi valori da 2000 a ${massimo}.`
    : null;
}

/** Maschera per trimestre o semestre: una sola cifra. */
export function mascheraCifraSingola(grezzo: string): string {
  return soleCifre(grezzo).slice(0, 1);
}

/** Valida un trimestre (1–4). */
export function validaTrimestre(valore: string): EsitoValidazione {
  if (!valore.trim()) return null;
  return /^[1-4]$/.test(valore.trim()) ? null : "Trimestre non valido: un numero da 1 a 4.";
}

/** Valida un semestre (1–2). */
export function validaSemestre(valore: string): EsitoValidazione {
  if (!valore.trim()) return null;
  return /^[12]$/.test(valore.trim()) ? null : "Semestre non valido: 1 oppure 2.";
}

// --------------------------------------------------------------------------- //
// Altri campi elementari
// --------------------------------------------------------------------------- //

/** Maschera per il codice fiscale di un ente: 11 cifre numeriche. */
export function mascheraCodiceFiscaleEnte(grezzo: string): string {
  return soleCifre(grezzo).slice(0, 11);
}

/** Valida il codice fiscale di un ente. */
export function validaCodiceFiscaleEnte(valore: string): EsitoValidazione {
  if (!valore.trim()) return null;
  return /^\d{11}$/.test(valore.trim()) ? null : "Il codice fiscale dell'ente e' di 11 cifre.";
}

/**
 * Valida un nome utente: minuscole, cifre e i separatori `.`, `_` e `-`, non in
 * testa ne' in coda, da 3 a 64 caratteri. E' la stessa regola dell'API
 * (`normalizza_nome_utente`), che confronta dopo aver portato tutto in minuscolo.
 */
export function validaNomeUtente(valore: string): EsitoValidazione {
  const nome = valore.trim().toLowerCase();
  if (!nome) return null;
  if (nome.length < 3 || nome.length > 64) {
    return "Il nome utente deve avere fra 3 e 64 caratteri.";
  }
  return /^[a-z0-9](?:[a-z0-9._-]*[a-z0-9])?$/.test(nome)
    ? null
    : "Usare lettere, cifre e i separatori «.», «_» e «-», senza spazi e non in testa o in coda.";
}

/** Valida un indirizzo di posta elettronica. */
export function validaEmail(valore: string): EsitoValidazione {
  if (!valore.trim()) return null;
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(valore.trim())
    ? null
    : "Indirizzo di posta elettronica non valido.";
}

/** Valida un URL assoluto. */
export function validaUrl(valore: string): EsitoValidazione {
  const testo = valore.trim();
  if (!testo) return null;
  try {
    const url = new URL(testo);
    return url.protocol === "http:" || url.protocol === "https:"
      ? null
      : "L'indirizzo deve iniziare con http:// o https://.";
  } catch {
    return "Indirizzo non valido: inserire un URL completo, per esempio https://www.dominio.it/percorso.";
  }
}

// --------------------------------------------------------------------------- //
// Peso dei file scelti localmente
// --------------------------------------------------------------------------- //

/**
 * Peso di un file **appena selezionato nel browser**, per la sola anteprima del
 * passo di caricamento.
 *
 * Il valore autorevole e' quello che l'API restituisce nel campo `peso` del
 * documento, gia' formattato da `format_peso_file`, ed e' quello che finisce nel
 * testo del link pubblico. Questa funzione non lo sostituisce mai: serve solo a
 * mostrare all'operatore che cosa sta per caricare.
 */
export function pesoLocale(byte: number): string {
  const kilobyte = byte / 1024;
  if (kilobyte < 1000) return `${virgolaDecimale(kilobyte)} Kb`;
  return `${virgolaDecimale(kilobyte / 1024)} Mb`;
}

/** Due decimali con la virgola, senza passare da `toLocaleString`. */
function virgolaDecimale(numero: number): string {
  const [intera, decimale = "00"] = numero.toFixed(2).split(".");
  return `${raggruppaMigliaia(intera)},${decimale}`;
}
